import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { motion } from 'framer-motion';
import { Plus, Mail, Phone, FileText, Trash2, Edit2, X } from 'lucide-react';
import { DndContext, closestCorners, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CRM = () => {
  const { token } = useAuth();
  const [phases, setPhases] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showPhaseModal, setShowPhaseModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
    phase_id: ''
  });
  const [newPhaseName, setNewPhaseName] = useState('');
  const [newPhaseColor, setNewPhaseColor] = useState('#64748B');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [phasesRes, leadsRes] = await Promise.all([
        axios.get(`${API_URL}/api/phases`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/leads`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setPhases(phasesRes.data);
      setLeads(leadsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load CRM data');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const leadId = active.id;
    const newPhaseId = over.id;

    try {
      await axios.put(
        `${API_URL}/api/leads/${leadId}`,
        { phase_id: newPhaseId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLeads(prevLeads =>
        prevLeads.map(lead =>
          lead.id === leadId ? { ...lead, phase_id: newPhaseId } : lead
        )
      );
      toast.success('Lead moved successfully');
    } catch (error) {
      toast.error('Failed to move lead');
    }
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    try {
      if (editingLead) {
        const response = await axios.put(
          `${API_URL}/api/leads/${editingLead.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setLeads(prevLeads =>
          prevLeads.map(lead => lead.id === editingLead.id ? response.data : lead)
        );
        toast.success('Lead updated successfully');
      } else {
        const response = await axios.post(
          `${API_URL}/api/leads`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setLeads([...leads, response.data]);
        toast.success('Lead created successfully');
      }
      resetForm();
      setShowLeadModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save lead');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;

    try {
      await axios.delete(`${API_URL}/api/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(leads.filter(lead => lead.id !== leadId));
      toast.success('Lead deleted successfully');
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const handleCreatePhase = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API_URL}/api/phases`,
        { name: newPhaseName, color: newPhaseColor },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPhases([...phases, response.data]);
      toast.success('Phase created successfully');
      setShowPhaseModal(false);
      setNewPhaseName('');
      setNewPhaseColor('#64748B');
    } catch (error) {
      toast.error('Failed to create phase');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      notes: '',
      phase_id: ''
    });
    setEditingLead(null);
  };

  const openEditModal = (lead) => {
    setEditingLead(lead);
    setFormData({
      name: lead.name,
      email: lead.email || '',
      phone: lead.phone || '',
      notes: lead.notes || '',
      phase_id: lead.phase_id
    });
    setShowLeadModal(true);
  };

  const getLeadsByPhase = (phaseId) => {
    return leads.filter(lead => lead.phase_id === phaseId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-12">
          <div className="text-slate-600">Loading CRM...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex items-center justify-between"
        >
          <div>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-testid="crm-title">
              CRM Dashboard
            </h1>
            <p className="text-lg text-slate-600">
              Manage your leads and track progress
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowPhaseModal(true)}
              className="bg-white border border-slate-200 text-slate-700 rounded-lg px-6 py-3 font-medium hover:bg-slate-50 smooth-transition flex items-center gap-2"
              data-testid="add-phase-button"
            >
              <Plus className="w-5 h-5" />
              Add Phase
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowLeadModal(true);
              }}
              className="bg-slate-900 text-white rounded-lg px-6 py-3 font-medium hover:bg-slate-800 smooth-transition flex items-center gap-2"
              data-testid="add-lead-button"
            >
              <Plus className="w-5 h-5" />
              Add Lead
            </button>
          </div>
        </motion.div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
            {phases.map((phase) => {
              const phaseLeads = getLeadsByPhase(phase.id);
              return (
                <div
                  key={phase.id}
                  id={phase.id}
                  className="flex-shrink-0 w-80 bg-white rounded-xl border border-slate-200 p-4"
                  data-testid={`phase-column-${phase.id}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: phase.color }}
                      />
                      <h3 className="font-display text-lg font-semibold text-slate-900">
                        {phase.name}
                      </h3>
                      <span className="text-sm text-slate-500">({phaseLeads.length})</span>
                    </div>
                  </div>

                  <div className="space-y-3 min-h-[200px]">
                    {phaseLeads.map((lead) => (
                      <motion.div
                        key={lead.id}
                        id={lead.id}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = 'move';
                          e.dataTransfer.setData('text/plain', lead.id);
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.add('bg-slate-50');
                        }}
                        onDragLeave={(e) => {
                          e.currentTarget.classList.remove('bg-slate-50');
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('bg-slate-50');
                          const draggedId = e.dataTransfer.getData('text/plain');
                          if (draggedId !== lead.id) {
                            handleDragEnd({ active: { id: draggedId }, over: { id: phase.id } });
                          }
                        }}
                        className="bg-slate-50 rounded-lg p-4 cursor-move hover:shadow-md smooth-transition group"
                        data-testid={`lead-${lead.id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="font-semibold text-slate-900">{lead.name}</h4>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 smooth-transition">
                            <button
                              onClick={() => openEditModal(lead)}
                              className="p-1 hover:bg-slate-200 rounded"
                              data-testid={`edit-lead-${lead.id}`}
                            >
                              <Edit2 className="w-4 h-4 text-slate-600" />
                            </button>
                            <button
                              onClick={() => handleDeleteLead(lead.id)}
                              className="p-1 hover:bg-red-100 rounded"
                              data-testid={`delete-lead-${lead.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                        </div>

                        {lead.email && (
                          <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                            <Mail className="w-4 h-4" />
                            <span className="truncate">{lead.email}</span>
                          </div>
                        )}

                        {lead.phone && (
                          <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                            <Phone className="w-4 h-4" />
                            <span>{lead.phone}</span>
                          </div>
                        )}

                        {lead.notes && (
                          <div className="flex items-start gap-2 text-sm text-slate-600 mt-3 pt-3 border-t border-slate-200">
                            <FileText className="w-4 h-4 mt-0.5" />
                            <p className="line-clamp-2">{lead.notes}</p>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>

                  <div
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add('bg-slate-100');
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove('bg-slate-100');
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('bg-slate-100');
                      const draggedId = e.dataTransfer.getData('text/plain');
                      handleDragEnd({ active: { id: draggedId }, over: { id: phase.id } });
                    }}
                    className="mt-4 p-4 border-2 border-dashed border-slate-200 rounded-lg text-center text-sm text-slate-400"
                  >
                    Drop lead here
                  </div>
                </div>
              );
            })}
          </div>
        </DndContext>
      </div>

      {showLeadModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6" data-testid="lead-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-lg w-full"
          >
            <div className="border-b border-slate-200 p-6 flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold text-slate-900">
                {editingLead ? 'Edit Lead' : 'Add New Lead'}
              </h2>
              <button
                onClick={() => {
                  setShowLeadModal(false);
                  resetForm();
                }}
                className="text-slate-400 hover:text-slate-600"
                data-testid="close-lead-modal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleCreateLead} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:border-slate-900 focus:ring-0"
                  placeholder="John Doe"
                  data-testid="lead-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:border-slate-900 focus:ring-0"
                  placeholder="john@example.com"
                  data-testid="lead-email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:border-slate-900 focus:ring-0"
                  placeholder="+91 98765 43210"
                  data-testid="lead-phone-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Phase *</label>
                <select
                  value={formData.phase_id}
                  onChange={(e) => setFormData({ ...formData, phase_id: e.target.value })}
                  required
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:border-slate-900 focus:ring-0"
                  data-testid="lead-phase-select"
                >
                  <option value="">Select a phase</option>
                  {phases.map(phase => (
                    <option key={phase.id} value={phase.id}>{phase.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-slate-900 focus:ring-0 resize-none"
                  placeholder="Add any additional notes..."
                  data-testid="lead-notes-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowLeadModal(false);
                    resetForm();
                  }}
                  className="flex-1 bg-slate-100 text-slate-700 rounded-lg px-6 py-3 font-medium hover:bg-slate-200"
                  data-testid="cancel-lead-button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-slate-900 text-white rounded-lg px-6 py-3 font-medium hover:bg-slate-800"
                  data-testid="save-lead-button"
                >
                  {editingLead ? 'Update Lead' : 'Create Lead'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {showPhaseModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6" data-testid="phase-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full"
          >
            <div className="border-b border-slate-200 p-6 flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold text-slate-900">Add New Phase</h2>
              <button
                onClick={() => setShowPhaseModal(false)}
                className="text-slate-400 hover:text-slate-600"
                data-testid="close-phase-modal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleCreatePhase} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Phase Name *</label>
                <input
                  type="text"
                  value={newPhaseName}
                  onChange={(e) => setNewPhaseName(e.target.value)}
                  required
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:border-slate-900 focus:ring-0"
                  placeholder="e.g., Follow Up"
                  data-testid="phase-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Color</label>
                <input
                  type="color"
                  value={newPhaseColor}
                  onChange={(e) => setNewPhaseColor(e.target.value)}
                  className="w-full h-11 px-2 rounded-lg border border-slate-200"
                  data-testid="phase-color-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPhaseModal(false)}
                  className="flex-1 bg-slate-100 text-slate-700 rounded-lg px-6 py-3 font-medium hover:bg-slate-200"
                  data-testid="cancel-phase-button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-slate-900 text-white rounded-lg px-6 py-3 font-medium hover:bg-slate-800"
                  data-testid="save-phase-button"
                >
                  Create Phase
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};